import { toast } from 'react-toastify';
import apiHelper from '../utils/apiHelper';
import axios from 'axios';

// Get the API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const handleApiError = (error, customMessage) => {
    console.error('handleApiError:', customMessage, error?.message);
    
    let errorMessage;
    
    // Handle different error structures
    if (error?.response?.data?.details) {
        errorMessage = error.response.data.details;
    } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
    } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
    } else if (error?.message) {
        errorMessage = error.message;
    } else if (typeof error === 'string') {
        errorMessage = error;
    } else {
        errorMessage = customMessage || 'An unknown error occurred';
    }
    
    toast.error(errorMessage);
    throw new Error(errorMessage);
};

// Helper function to construct media URLs
const getMediaUrl = (fileName) => {
    if (!fileName) return '/placeholder-thumbnail.jpg';
    // Instead of returning just the URL, we'll return an object with the URL and headers
    return {
        url: `${API_BASE_URL}/api/graph/getMediaContent/${fileName}`,
        headers: apiHelper.getHeaders('getMediaContent')
    };
};

// Add this method to galleryService
const getGalleryThumbnail = async (galleryId) => {
    try {
        const items = await galleryService.getGalleryMedia(galleryId);
        if (!items.length) return null;

        // Get the first item
        const firstItem = items[0];
        const mimeType = firstItem.mimeType?.toLowerCase() || '';

        // For images, fetch and create blob URL
        if (mimeType.startsWith('image/')) {
            const headers = await apiHelper.getHeaders('getMediaContent');
            const response = await fetch(firstItem.mediaUrl, { headers });
            
            if (!response.ok) {
                throw new Error('Failed to fetch image');
            }

            const blob = await response.blob();
            return URL.createObjectURL(blob);
        }
        
        // For videos, try to get a video thumbnail or return a placeholder
        else if (mimeType.startsWith('video/')) {
            // If there's a thumbnail property, use it
            if (firstItem.thumbnail) {
                const headers = await apiHelper.getHeaders('getMediaContent');
                const response = await fetch(firstItem.thumbnail, { headers });
                
                if (response.ok) {
                    const blob = await response.blob();
                    return URL.createObjectURL(blob);
                }
            }
            // Return a video placeholder URL
            return '/video-placeholder.jpg';
        }
        
        // For audio, return an audio placeholder
        else if (mimeType.startsWith('audio/')) {
            return '/audio-placeholder.jpg';
        }
        
        // For any other type, return a generic placeholder
        return '/placeholder-thumbnail.jpg';
    } catch (error) {
        console.error('Error getting gallery thumbnail:', error);
        return null;
    }
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const itemsCache = new Map();
const mediaCache = new Map();
let pendingFetches = new Map();

// Add request deduplication for media content
const pendingMediaFetches = new Map();

const childrenCache = new Map();

// Add at the top with other cache variables
const pendingGalleryFetches = new Map();

export const galleryService = {

    getGalleries: async (forceRefresh = false) => {
              
        try {
            // Check for pending request first
            if (pendingGalleryFetches.has('galleries')) {
                const result = await pendingGalleryFetches.get('galleries');
                return result;
            }

            // Check cache first (only if not forcing refresh)
            if (!forceRefresh) {
                if (itemsCache.has('galleries')) {
                    const cached = itemsCache.get('galleries');
                    const age = Date.now() - cached.timestamp;
                    
                    if (age < CACHE_DURATION) {
                        return cached.items;
                    }
                }
            }

            // Create new fetch promise
            const fetchPromise = (async () => {
                try {
                    const response = await apiHelper.fetchJSONData('api/graph/getAllGalleries');
                    
                    if (!response.galleries || !Array.isArray(response.galleries)) {
                        console.error('Invalid gallery data format:', response);
                        return [];
                    }
                    
                    const galleries = response.galleries.map(gallery => ({
                        id: gallery.id,
                        title: gallery.title || gallery.Title || '',
                        description: gallery.description || gallery.Description || '',
                        type: (gallery.type || gallery.GalleryType || 'image').toLowerCase(),
                        itemCount: gallery.itemCount || 0
                    }));

                    // Cache the galleries
                    itemsCache.set('galleries', {
                        items: galleries,
                        timestamp: Date.now()
                    });
                    
                    
                    return galleries;
                } catch (error) {
                    console.error('Error fetching galleries:', error);
                    throw error;
                }
            })();

            // Store the pending fetch
            pendingGalleryFetches.set('galleries', fetchPromise);

            try {
                const result = await fetchPromise;
                return result;
            } finally {
                // Clean up pending fetch after a short delay
                setTimeout(() => {
                    pendingGalleryFetches.delete('galleries');
                }, 1000);
            }
            
        } catch (error) {
            pendingGalleryFetches.delete('galleries');
            console.error('Gallery fetch error:', error);
            throw error;
        }
    },

    // Create a new gallery
    createGallery: async (galleryData) => {
        try {
           
            
            // Use apiHelper instead of direct fetch
            const response = await apiHelper.post(`/api/graph/addGallery`, galleryData);
            
           
            
            // Handle SharePoint response structure
            let gallery;
            if (response.gallery) {
                gallery = response.gallery;
            } else if (response.data && response.data.gallery) {
                gallery = response.data.gallery;
            } else if (response.data) {
                gallery = response.data;
            } else {
                gallery = response;
            }
            
           
            
            // SharePoint returns the item with an 'id' field and 'fields' object
            const galleryId = gallery.id || gallery.Id;
            const galleryFields = gallery.fields || gallery;
            
            if (!galleryId) {
                console.error('Invalid gallery response structure:', response);
                throw new Error('Invalid response from server - no gallery ID returned');
            }
            
            // Normalize the returned gallery object to match our expected format
            const normalizedGallery = {
                id: galleryId,
                title: galleryFields.Title || galleryData.title || '',
                description: galleryFields.Description || galleryData.description || '',
                type: (galleryFields.GalleryType || galleryData.galleryType || 'image').toLowerCase(),
                itemCount: 0 // New galleries start with 0 items
            };
            
           
            return normalizedGallery;
            
        } catch (error) {
            console.error('createGallery error:', error);
            
            // If it's our custom error about invalid response, provide more context
            if (error.message.includes('Invalid response from server')) {
                handleApiError(error, 'Server returned an unexpected response format. Please try again or contact support.');
            } else {
                handleApiError(error, 'Failed to create gallery');
            }
        }
    },

    // Get media items for a specific gallery
    getGalleryMedia: async (galleryId) => {
        try {
            const headers = await apiHelper.getHeaders('getGalleryMedia');
            
            const response = await fetch(`${API_BASE_URL}/api/graph/getGalleryMedia/${galleryId}`, {
                headers
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error('Failed to fetch gallery media');
            }
            
            const data = await response.json();
            const mediaHeaders = await apiHelper.getHeaders('getMediaContent');
            
            const filesWithUrls = data.files?.map(file => ({
                ...file,
                // Use the mediaUrl from API response (includes streaming URLs for videos)
                mediaUrl: file.mediaUrl?.startsWith('/') 
                    ? `${API_BASE_URL}${file.mediaUrl}` 
                    : file.mediaUrl,
                url: file.mediaUrl?.startsWith('/') 
                    ? `${API_BASE_URL}${file.mediaUrl}` 
                    : file.mediaUrl,
                mimeType: file.type || file.Type || file.mimeType || 'unknown',
                headers: mediaHeaders
            })) || [];

            return filesWithUrls;
        } catch (error) {
            console.error('Full gallery media error:', error);
            handleApiError(error, 'Failed to load gallery media');
            return [];
        }
    },

    // Update a gallery
    updateGallery: async (galleryId, galleryData) => {
        try {
            const response = await apiHelper.put(`graph/gallery/${galleryId}`, {
                title: galleryData.title,
                description: galleryData.description
            });
            
            toast.success('Gallery updated successfully');
            return {
                ...galleryData,
                id: galleryId,
                Title: galleryData.title,        // Match SharePoint field names
                Description: galleryData.description
            };
        } catch (error) {
            handleApiError(error, 'Failed to update gallery');
        }
    },

    // Update the deleteGallery method
    deleteGallery: async (galleryId) => {
        try {
            const endpoint = `api/graph/deleteGalleryAndItems/${galleryId}`;
            const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
                method: 'DELETE',
                headers: await apiHelper.getHeaders(endpoint)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success;
        } catch (error) {
            console.error('Error in deleteGallery:', error);
            throw error;
        }
    },

    // New method for getting gallery stats
    getGalleryStats: async (galleryId) => {
        try {
            const items = await galleryService.getGalleryMedia(galleryId);
            return {
                itemCount: items.length,
                items: items
            };
        } catch (error) {
            console.error('Error getting gallery stats:', error);
            return {
                itemCount: 0,
                items: []
            };
        }
    },

    // Update the deleteGalleryItem method
    deleteGalleryItem: async (galleryId, mediaId) => {
        try {
            const endpoint = `api/graph/deleteGalleryItem/${galleryId}/${mediaId}`;
            const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
                method: 'DELETE',
                headers: await apiHelper.getHeaders(endpoint)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error in deleteGalleryItem:', error);
            throw error;
        }
    },

    updateGalleryItemCount: async (galleryId) => {
        try {
            const items = await galleryService.getGalleryMedia(galleryId);
            return items.length;
        } catch (error) {
            console.error('Error updating gallery item count:', error);
            return 0;
        }
    },

    // Add a new method for creating news items
    createNewsItem: async (articleData) => {
        try {
            const API_BASE_URL = process.env.REACT_APP_API_URL;
            if (!API_BASE_URL) {
                throw new Error('API URL is not configured');
            }

            // Convert boolean values to strings as expected by the API
            const response = await apiHelper.post(`${API_BASE_URL}/api/graph/addNewsItem`, {
                Title: articleData.title,
                Content: articleData.content,
                WeeklySection: articleData.weeklySection || '',
                IsFeatured: String(articleData.isFeatured),  // Convert to string
                IsWeeklyNews: String(articleData.isWeeklyNews),  // Convert to string
                IsSubmittedArticle: String(articleData.isSubmittedArticle),  // Convert to string
                Status: articleData.status,
                Image: articleData.imageName || '',
                Video: articleData.videoName || '',
                Podcast: articleData.podcastName || '',
                File: articleData.pdfName || '',
                IssueDate: articleData.issueDate,
                Author0: articleData.author,
                Company: articleData.submittedCompany || '',
                StrategicCategory: articleData.strategicCategory || ''
            });
            
            return response.data;
        } catch (error) {
            console.error('Error creating news item:', error);
            // Add more detailed error logging
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
            }
            throw error;
        }
    },



    checkNewsItem: async (itemId, type) => {
        try {
           
            const { data } = await apiHelper.get(`${API_BASE_URL}/api/graph/checkNewsItem/${itemId}?type=${type}`);
            return data;
        } catch (error) {
            return false;
        }
    },

    getGalleryThumbnail,

    getGalleryItems: async (galleryId, forceRefresh = false) => {
        if (!galleryId) return [];
        
        try {
            // Check if there's already a pending fetch for this gallery
            if (pendingFetches.has(galleryId)) {
                const result = await pendingFetches.get(galleryId);
                return result;
            }

            // Check cache first (only if not forcing refresh)
            if (!forceRefresh) {
                const cached = itemsCache.get(galleryId);
                if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
                    return cached.items;
                }
            }

            // Create a new fetch promise
            const fetchPromise = (async () => {
                const items = await galleryService.getGalleryMedia(galleryId);
                
                // Store in cache
                itemsCache.set(galleryId, {
                    items,
                    timestamp: Date.now()
                });

                return items;
            })();

            // Store the pending fetch
            pendingFetches.set(galleryId, fetchPromise);

            // Wait for the result
            const result = await fetchPromise;

            // Clean up the pending fetch
            pendingFetches.delete(galleryId);

            return result;

        } catch (error) {
            // Clean up the pending fetch on error
            pendingFetches.delete(galleryId);
            console.error('Error fetching gallery items:', error);
            throw error;
        }
    },

    // Optional: Add a method to clear cache if needed
    clearCache: (galleryId) => {
        if (galleryId) {
            itemsCache.delete(galleryId);
        } else {
            itemsCache.clear();
            mediaCache.clear();
            pendingFetches.clear();
            pendingMediaFetches.clear();
            pendingGalleryFetches.clear();
        }
    },

    // Update the getMediaContent method
    getMediaContent: async (url, headers, signal) => {
        const cacheKey = url;
        
        try {
            // Check for pending requests first
            if (pendingMediaFetches.has(cacheKey)) {
                const pendingResult = await pendingMediaFetches.get(cacheKey);
                return pendingResult.clone();
            }

            // Check memory cache
            if (mediaCache.has(cacheKey)) {
                const cached = mediaCache.get(cacheKey);
                const age = Date.now() - cached.timestamp;
                
                if (age < CACHE_DURATION) {
                    return cached.response.clone();
                }
                mediaCache.delete(cacheKey);
            }

            // Create new fetch promise
            const fetchPromise = (async () => {
                // Get fresh headers for each request
                const freshHeaders = await apiHelper.getHeaders('getMediaContent');
                
                const response = await fetch(url, { 
                    headers: freshHeaders, // Use fresh headers instead of passed headers
                    signal,
                    credentials: 'include', // Include credentials in the request
                    mode: 'cors' // Explicitly set CORS mode
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch media content: ${response.status}`);
                }

                // Store in cache
                mediaCache.set(cacheKey, {
                    response: response.clone(),
                    timestamp: Date.now()
                });

                return response;
            })();

            // Store the pending fetch
            pendingMediaFetches.set(cacheKey, fetchPromise);

            try {
                const response = await fetchPromise;
                return response.clone(); // Return a clone of the response
            } finally {
                // Clean up pending fetch after a short delay
                setTimeout(() => {
                    pendingMediaFetches.delete(cacheKey);
                }, 1000);
            }

        } catch (error) {
            pendingMediaFetches.delete(cacheKey);
            if (error.name === 'AbortError') {
                throw error;
            }
            console.error('Media fetch error:', error);
            throw new Error(`Failed to load media: ${error.message}`);
        }
    },

    // Add method to clear media cache
    clearMediaCache: () => {
        mediaCache.clear();
    },

    // Add a method to pre-warm the cache
    preloadGalleryThumbnails: async (galleryId) => {
        try {
            const items = await galleryService.getGalleryItems(galleryId);
            // Preload first 4 images
            const preloadPromises = items
                .slice(0, 4)
                .map(item => galleryService.getMediaContent(item.mediaUrl, item.headers));
            await Promise.all(preloadPromises);
        } catch (error) {
            console.warn('Failed to preload thumbnails:', error);
        }
    },

    // Add a method to handle children requests
    getChildren: async (driveId) => {
        const cacheKey = `children-${driveId}`;

        if (childrenCache.has(cacheKey)) {
            const cached = childrenCache.get(cacheKey);
            const age = Date.now() - cached.timestamp;

            if (age < CACHE_DURATION) {
                return cached.data;
            }
            childrenCache.delete(cacheKey);
        }

        const headers = await apiHelper.getHeaders('getChildren');
        const response = await fetch(`${API_BASE_URL}/api/graph/getChildren/${driveId}`, {
            headers
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch children: ${response.status}`);
        }

        const data = await response.json();
        childrenCache.set(cacheKey, {
            data,
            timestamp: Date.now()
        });

        return data;
    },

    clearCache: () => {
        itemsCache.clear();
        mediaCache.clear();
        childrenCache.clear();
    },

    // Add this method to galleryService
    getVideoThumbnail: async (videoUrl, headers) => {
        const cacheKey = `thumb-${videoUrl}`;
        
        // Check cache first
        if (mediaCache.has(cacheKey)) {
            const cached = mediaCache.get(cacheKey);
            if (Date.now() - cached.timestamp < CACHE_DURATION) {
                return cached.url;
            }
            mediaCache.delete(cacheKey);
        }

        try {
            // Skip metadata check and go straight to generating thumbnail
            const video = document.createElement('video');
            video.crossOrigin = 'anonymous';
            video.src = videoUrl;
            
            return new Promise((resolve) => {
                video.addEventListener('loadeddata', () => {
                    video.currentTime = 1; // Seek to 1 second
                });

                video.addEventListener('seeked', () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0);
                    
                    const url = canvas.toDataURL('image/jpeg', 0.8);
                    mediaCache.set(cacheKey, {
                        url,
                        timestamp: Date.now()
                    });
                    
                    resolve(url);
                    video.remove();
                });

                // Add error handling
                video.addEventListener('error', () => {
                    console.warn('Video thumbnail generation failed');
                    resolve(null);
                    video.remove();
                });

                // Add timeout
                setTimeout(() => {
                    console.warn('Video thumbnail generation timed out');
                    resolve(null);
                    video.remove();
                }, 10000); // 10 second timeout
            });
        } catch (error) {
            console.warn('Failed to generate video thumbnail:', error);
            return null;
        }
    },

    uploadMedia: async (formData, onProgress) => {
        try {
            const headers = await apiHelper.getHeaders('uploadGalleryMedia');
            delete headers['Content-Type'];

            const response = await axios.post(
                `${API_BASE_URL}/api/graph/uploadGalleryMedia`,
                formData,
                {
                    headers,
                    onUploadProgress: (event) => {
                        if (!event.total) return;

                        const percent = (event.loaded / event.total) * 100;

                        if (typeof onProgress === 'function') {
                            onProgress(percent);
                        }
                    }
                }
            );

            return response.data;

        } catch (error) {
            console.error('Upload error:', error);
            throw new Error(
                error.response?.data?.error ||
                error.response?.data?.details ||
                'Upload failed'
            );
        }
    },


    clearThumbnailCache: (galleryId) => {
        // Clear specific gallery thumbnail from cache
        if (galleryId && mediaCache.has(galleryId)) {
            mediaCache.delete(galleryId);
        }
    },
}; 

// Add global function for debugging/manual cache clearing
window.clearGalleryCache = () => {
    galleryService.clearCache();        
    return 'Cache cleared successfully';
};

// Add global function to force refresh specific gallery
window.refreshGallery = async (galleryId) => {
    try {
        galleryService.clearCache();
        const freshData = await galleryService.getGalleryMedia(galleryId);
        
        return freshData;
    } catch (error) {
        console.error('Failed to refresh gallery:', error);
        throw error;
    }
}; 